import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AdminSidebar from "./AdminSidebar";
import AdminTopbar from "./Admintopbar";

import {
  getStudents,
  getAdminCourses,
  addStudent,
  deleteStudent,
  getStudentExamAttempts,
  sendAdminMessage,
} from "../api/adminclient";


/* ============================================================
   CONSTANTS
============================================================ */

const emptyForm = {
  fullName: "",
  email: "",
  mobile: "",
  courses: [],
  status: "Active",
};

const emptyMessageForm = {
  studentId: "",
  email: "",
  subject: "",
  body: "",
  includeExamPdf: false,
  attemptId: "",
};

const MESSAGE_MAX_LEN = 1000;
const PAGE_SIZE = 8;


/* ============================================================
   DATE / TIME HELPERS
============================================================ */

/*
  Django returns registeredAt as an ISO timestamp, normally UTC.

  Example:
  2026-10-03T05:27:56.925744Z

  This function converts it to India/Kerala time.

  Example:
  03/10/2026, 10:57:56 AM
*/

const formatRegisteredDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(date);
};


/*
  Used for exam attempt dates in the message modal.
*/

const formatAttemptDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};


/* ============================================================
   COMPONENT
============================================================ */

export default function AdminStudents() {
  const navigate = useNavigate();


  /* ==========================================================
     STUDENTS
  ========================================================== */

  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");


  /* ==========================================================
     FILTERS
  ========================================================== */

  const [search, setSearch] = useState("");
  const [courseFilter, setCourseFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");


  /* ==========================================================
     PAGINATION
  ========================================================== */

  const [currentPage, setCurrentPage] = useState(1);


  /* ==========================================================
     ADD STUDENT
  ========================================================== */

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);


  /* ==========================================================
     SEND MESSAGE
  ========================================================== */

  const [showMessage, setShowMessage] = useState(false);

  const [messageForm, setMessageForm] = useState(
    emptyMessageForm
  );

  const [messageError, setMessageError] = useState("");

  const [sendingMessage, setSendingMessage] = useState(false);

  const [messageSent, setMessageSent] = useState(false);


  /* ==========================================================
     EXAM ATTEMPTS
  ========================================================== */

  const [examAttempts, setExamAttempts] = useState([]);

  const [loadingAttempts, setLoadingAttempts] = useState(false);


  /* ==========================================================
     LOAD STUDENTS
  ========================================================== */

  const load = () => {
    setLoading(true);
    setError("");

    Promise.all([
      getStudents(),
      getAdminCourses(),
    ])
      .then(([studentsResponse, coursesResponse]) => {
        const studentData = Array.isArray(
          studentsResponse?.data
        )
          ? studentsResponse.data
          : [];

        const courseData = Array.isArray(
          coursesResponse?.data
        )
          ? coursesResponse.data
          : [];

        setStudents(studentData);
        setCourses(courseData);
      })
      .catch((err) => {
        console.error("Could not load students:", err);

        setError(
          "Could not load students. Please check that the Django backend is running."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };


  useEffect(() => {
    load();
  }, []);


  /* ==========================================================
     COURSE NAMES
  ========================================================== */

  const courseNames = useMemo(() => {
    return Array.from(
      new Set(
        courses
          .map((course) => course.name)
          .filter(Boolean)
      )
    );
  }, [courses]);


  /* ==========================================================
     FILTER STUDENTS
  ========================================================== */

  const filtered = useMemo(() => {
    return students.filter((student) => {
      /*
        Django StudentAdminSerializer returns:
          name
          email
          mobile
          courseNames
          registeredAt

        So we use those fields directly.
      */

      const studentCourses = Array.isArray(
        student.courseNames
      )
        ? student.courseNames
        : [];

      const studentName = student.name || "";
      const studentEmail = student.email || "";
      const studentMobile = student.mobile || "";

      const studentStatus =
        student.status || "Active";

      const searchValue = search
        .trim()
        .toLowerCase();

      const matchesSearch =
        !searchValue ||
        studentName
          .toLowerCase()
          .includes(searchValue) ||
        studentEmail
          .toLowerCase()
          .includes(searchValue) ||
        String(studentMobile)
          .toLowerCase()
          .includes(searchValue);

      const matchesCourse =
        courseFilter === "All" ||
        studentCourses.includes(courseFilter);

      const matchesStatus =
        statusFilter === "All" ||
        studentStatus === statusFilter;

      return (
        matchesSearch &&
        matchesCourse &&
        matchesStatus
      );
    });
  }, [
    students,
    search,
    courseFilter,
    statusFilter,
  ]);


  /* ==========================================================
     RESET PAGE WHEN FILTER CHANGES
  ========================================================== */

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    courseFilter,
    statusFilter,
  ]);


  /* ==========================================================
     PAGINATION
  ========================================================== */

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / PAGE_SIZE)
  );


  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [
    currentPage,
    totalPages,
  ]);


  const paginated = useMemo(() => {
    const start =
      (currentPage - 1) * PAGE_SIZE;

    return filtered.slice(
      start,
      start + PAGE_SIZE
    );
  }, [
    filtered,
    currentPage,
  ]);


  const goToPage = (page) => {
    if (
      page < 1 ||
      page > totalPages
    ) {
      return;
    }

    setCurrentPage(page);
  };


  /* ==========================================================
     ADD STUDENT
  ========================================================== */

  const openAdd = () => {
    setForm({
      ...emptyForm,
      courses: [],
    });

    setFormError("");
    setShowAdd(true);
  };


  const toggleCourse = (course) => {
    setForm((current) => {
      const hasCourse =
        current.courses.includes(course);

      return {
        ...current,
        courses: hasCourse
          ? current.courses.filter(
              (item) => item !== course
            )
          : [
              ...current.courses,
              course,
            ],
      };
    });
  };


  const saveNewStudent = async () => {
    setFormError("");

    if (
      !form.fullName.trim() ||
      !form.email.trim()
    ) {
      setFormError(
        "Name and email are required."
      );
      return;
    }

    setSaving(true);

    try {
      await addStudent({
        ...form,
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
      });

      setShowAdd(false);

      setForm({
        ...emptyForm,
        courses: [],
      });

      load();
    } catch (err) {
      console.error(
        "Could not add student:",
        err
      );

      setFormError(
        err?.response?.data?.detail ||
          "Could not add student."
      );
    } finally {
      setSaving(false);
    }
  };


  /* ==========================================================
     DELETE STUDENT
  ========================================================== */

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Remove this student? This cannot be undone."
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteStudent(id);

      load();
    } catch (err) {
      console.error(
        "Could not delete student:",
        err
      );

      setError(
        err?.response?.data?.detail ||
          "Could not delete student."
      );
    }
  };


  /* ==========================================================
     LOAD EXAM ATTEMPTS
  ========================================================== */

  const loadExamAttempts = async (
    studentId
  ) => {
    if (!studentId) {
      setExamAttempts([]);
      return;
    }

    setLoadingAttempts(true);
    setExamAttempts([]);

    try {
      const response =
        await getStudentExamAttempts(
          studentId
        );

      const data = Array.isArray(
        response?.data
      )
        ? response.data
        : [];

      setExamAttempts(data);
    } catch (err) {
      console.error(
        "Could not load exam attempts:",
        err
      );

      setExamAttempts([]);

      setMessageError(
        "Could not load this student's exam attempts."
      );
    } finally {
      setLoadingAttempts(false);
    }
  };


  /* ==========================================================
     OPEN MESSAGE MODAL
  ========================================================== */

  const openMessageModal = (
    student = null
  ) => {
    setMessageError("");
    setMessageSent(false);

    setExamAttempts([]);

    if (student) {
      const nextForm = {
        ...emptyMessageForm,
        studentId: String(student.id),
        email: student.email || "",
      };

      setMessageForm(nextForm);

      setShowMessage(true);

      loadExamAttempts(student.id);

      return;
    }

    /*
      Page-level Send Message button:
      Open the modal without selecting a student.
      Admin can then select one from the dropdown.
    */

    setMessageForm({
      ...emptyMessageForm,
    });

    setShowMessage(true);
  };


  /* ==========================================================
     CLOSE MESSAGE MODAL
  ========================================================== */

  const closeMessageModal = () => {
    if (sendingMessage) {
      return;
    }

    setShowMessage(false);

    setMessageError("");
    setMessageSent(false);

    setExamAttempts([]);

    setMessageForm({
      ...emptyMessageForm,
    });
  };


  /* ==========================================================
     CHANGE MESSAGE FIELD
  ========================================================== */

  const updateMessageField = (
    field,
    value
  ) => {
    setMessageForm((current) => ({
      ...current,
      [field]: value,
    }));
  };


  /* ==========================================================
     SELECT STUDENT FOR MESSAGE
  ========================================================== */

  const handleMessageStudentChange = (
    event
  ) => {
    const studentId =
      event.target.value;

    const selectedStudent =
      students.find(
        (student) =>
          String(student.id) ===
          String(studentId)
      );

    setMessageError("");

    setExamAttempts([]);

    setMessageForm((current) => ({
      ...current,
      studentId,
      email:
        selectedStudent?.email || "",
      attemptId: "",
      includeExamPdf: false,
    }));

    if (studentId) {
      loadExamAttempts(studentId);
    }
  };


  /* ==========================================================
     SEND MESSAGE
  ========================================================== */

  const handleSendMessage = async () => {
    setMessageError("");

    /*
      Student ID is mandatory.
    */

    if (!messageForm.studentId) {
      setMessageError(
        "Please select a student."
      );
      return;
    }


    /*
      Subject validation.
    */

    if (!messageForm.subject.trim()) {
      setMessageError(
        "Subject is required."
      );
      return;
    }


    /*
      Message validation.
    */

    if (!messageForm.body.trim()) {
      setMessageError(
        "Message body is required."
      );
      return;
    }


    /*
      If PDF is selected,
      an attempt must also be selected.
    */

    if (
      messageForm.includeExamPdf &&
      !messageForm.attemptId
    ) {
      setMessageError(
        "Please select an exam attempt for the PDF attachment."
      );
      return;
    }


    setSendingMessage(true);

    try {
      const payload = {
        student_id: Number(
          messageForm.studentId
        ),

        subject:
          messageForm.subject.trim(),

        message:
          messageForm.body.trim(),

        include_exam_pdf:
          Boolean(
            messageForm.includeExamPdf
          ),

        attempt_id:
          messageForm.attemptId
            ? Number(
                messageForm.attemptId
              )
            : null,
      };

      console.log(
        "Sending admin message:",
        payload
      );

      await sendAdminMessage(
        payload
      );

      setMessageSent(true);

      setTimeout(() => {
        setShowMessage(false);

        setMessageForm({
          ...emptyMessageForm,
        });

        setExamAttempts([]);

        setMessageSent(false);
      }, 1200);
    } catch (err) {
      console.error(
        "Send message error:",
        err
      );

      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Could not send the message. Please try again.";

      setMessageError(detail);
    } finally {
      setSendingMessage(false);
    }
  };


  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <div className="admin-page-full d-flex">

      <AdminSidebar />

      <div className="admin-content">

        <AdminTopbar />

        {/* ====================================================
            PAGE HEADER
        ==================================================== */}

        <div className="d-flex justify-content-between align-items-center mb-3">

          <h2 className="h5 fw-bold mb-0">
            Students
          </h2>

          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={() =>
              openMessageModal()
            }
          >
            <i className="bi bi-envelope" />
            Send Message
          </button>

        </div>


        {/* ====================================================
            ERROR
        ==================================================== */}

        {error && (
          <div className="alert alert-warning py-2 small">
            {error}
          </div>
        )}


        {/* ====================================================
            FILTERS
        ==================================================== */}

        <div className="admin-card p-3 mb-3">

          <div className="row g-2">

            {/* Search */}

            <div className="col-12 col-md-5">

              <div className="input-group">

                <span className="input-group-text bg-white">
                  <i className="bi bi-search" />
                </span>

                <input
                  className="form-control"
                  placeholder="Search by name, email or mobile..."
                  value={search}
                  onChange={(event) =>
                    setSearch(
                      event.target.value
                    )
                  }
                />

              </div>

            </div>


            {/* Course */}

            <div className="col-6 col-md-3">

              <select
                className="form-select"
                value={courseFilter}
                onChange={(event) =>
                  setCourseFilter(
                    event.target.value
                  )
                }
              >

                <option value="All">
                  All Courses
                </option>

                {courseNames.map(
                  (courseName) => (
                    <option
                      key={courseName}
                      value={courseName}
                    >
                      {courseName}
                    </option>
                  )
                )}

              </select>

            </div>


            {/* Status */}

            <div className="col-6 col-md-3">

              <select
                className="form-select"
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
              >

                <option value="All">
                  All Status
                </option>

                <option value="Active">
                  Active
                </option>

                <option value="Inactive">
                  Inactive
                </option>

              </select>

            </div>

          </div>

        </div>


        {/* ====================================================
            STUDENT TABLE
        ==================================================== */}

        <div className="admin-card p-3 p-md-4">

          {loading && (
            <p className="text-muted mb-0">
              Loading students...
            </p>
          )}


          {!loading &&
            filtered.length === 0 && (
              <p className="text-muted small mb-0">
                No students match your
                filters.
              </p>
            )}


          {!loading &&
            filtered.length > 0 && (
              <>
                <div className="table-responsive">

                  <table className="table align-middle admin-table mb-0">

                    <thead>

                      <tr>

                        <th>
                          #
                        </th>

                        <th>
                          Name
                        </th>

                        <th>
                          Email
                        </th>

                        <th>
                          Mobile
                        </th>

                        <th>
                          Courses
                        </th>

                        <th>
                          Status
                        </th>

                        <th>
                          Registered
                        </th>

                        <th className="text-end">
                          Actions
                        </th>

                      </tr>

                    </thead>


                    <tbody>

                      {paginated.map(
                        (student, index) => {

                          /*
                            Django field:
                            courseNames
                          */

                          const studentCourses =
                            Array.isArray(
                              student.courseNames
                            )
                              ? student.courseNames
                              : [];


                          /*
                            Status is not currently
                            included by the Django
                            serializer, so Active
                            remains the fallback.
                          */

                          const status =
                            student.status ||
                            "Active";


                          const rowNumber =
                            (currentPage - 1) *
                              PAGE_SIZE +
                            index +
                            1;


                          return (
                            <tr
                              key={
                                student.id
                              }
                            >

                              {/* # */}

                              <td className="text-muted">
                                {rowNumber}
                              </td>


                              {/* NAME */}

                              <td className="fw-semibold">
                                {student.name ||
                                  "—"}
                              </td>


                              {/* EMAIL */}

                              <td className="text-muted">
                                {student.email ||
                                  "—"}
                              </td>


                              {/* MOBILE */}

                              <td className="text-muted">
                                {student.mobile ||
                                  "—"}
                              </td>


                              {/* COURSES */}

                              <td>
                                {studentCourses.length >
                                0
                                  ? studentCourses.join(
                                      ", "
                                    )
                                  : "—"}
                              </td>


                              {/* STATUS */}

                              <td>

                                <span
                                  className={`admin-status-pill ${String(
                                    status
                                  ).toLowerCase()}`}
                                >
                                  {status}
                                </span>

                              </td>


                              {/* REGISTERED */}

                              <td className="text-muted small">

                                {formatRegisteredDate(
                                  student.registeredAt
                                )}

                              </td>


                              {/* ACTIONS */}

                              <td className="text-end">

                                {/* Message */}

                                <button
                                  className="btn btn-sm btn-outline-secondary me-1"
                                  title="Message"
                                  onClick={() =>
                                    openMessageModal(
                                      student
                                    )
                                  }
                                >
                                  <i className="bi bi-envelope" />
                                </button>


                                {/* View */}

                                <button
                                  className="btn btn-sm btn-outline-secondary me-1"
                                  title="View"
                                  onClick={() =>
                                    navigate(
                                      `/admin/students/${student.id}`
                                    )
                                  }
                                >
                                  <i className="bi bi-eye" />
                                </button>


                                {/* Delete */}

                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete"
                                  onClick={() =>
                                    handleDelete(
                                      student.id
                                    )
                                  }
                                >
                                  <i className="bi bi-trash" />
                                </button>

                              </td>

                            </tr>
                          );
                        }
                      )}

                    </tbody>

                  </table>

                </div>


                {/* ==================================================
                    PAGINATION
                ================================================== */}

                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">

                  <p className="text-muted small mb-0">

                    Showing{" "}

                    {(currentPage - 1) *
                      PAGE_SIZE +
                      1}

                    –

                    {Math.min(
                      currentPage *
                        PAGE_SIZE,
                      filtered.length
                    )}

                    {" "}of{" "}

                    {filtered.length}

                  </p>


                  <nav aria-label="Students pagination">

                    <ul className="pagination pagination-sm mb-0">

                      {/* Previous */}

                      <li
                        className={`page-item ${
                          currentPage === 1
                            ? "disabled"
                            : ""
                        }`}
                      >

                        <button
                          className="page-link"
                          onClick={() =>
                            goToPage(
                              currentPage - 1
                            )
                          }
                        >
                          <i className="bi bi-chevron-left" />
                        </button>

                      </li>


                      {/* Pages */}

                      {Array.from(
                        {
                          length:
                            totalPages,
                        },
                        (_, index) =>
                          index + 1
                      ).map(
                        (page) => (
                          <li
                            key={page}
                            className={`page-item ${
                              page ===
                              currentPage
                                ? "active"
                                : ""
                            }`}
                          >

                            <button
                              className="page-link"
                              onClick={() =>
                                goToPage(
                                  page
                                )
                              }
                            >
                              {page}
                            </button>

                          </li>
                        )
                      )}


                      {/* Next */}

                      <li
                        className={`page-item ${
                          currentPage ===
                          totalPages
                            ? "disabled"
                            : ""
                        }`}
                      >

                        <button
                          className="page-link"
                          onClick={() =>
                            goToPage(
                              currentPage + 1
                            )
                          }
                        >
                          <i className="bi bi-chevron-right" />
                        </button>

                      </li>

                    </ul>

                  </nav>

                </div>

              </>
            )}

        </div>

      </div>


      {/* ======================================================
          ADD STUDENT MODAL
      ====================================================== */}

      {showAdd && (

        <div
          className="modal d-block"
          tabIndex={-1}
          style={{
            background:
              "rgba(0,0,0,0.45)",
          }}
        >

          <div className="modal-dialog modal-dialog-centered">

            <div className="modal-content">

              <div className="modal-header">

                <h5 className="modal-title">
                  Add Student
                </h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() =>
                    setShowAdd(false)
                  }
                />

              </div>


              <div className="modal-body">

                {/* Full Name */}

                <div className="mb-3">

                  <label className="form-label small text-muted">
                    Full Name
                  </label>

                  <input
                    className="form-control"
                    value={form.fullName}
                    onChange={(event) =>
                      setForm(
                        (current) => ({
                          ...current,
                          fullName:
                            event.target
                              .value,
                        })
                      )
                    }
                  />

                </div>


                {/* Email / Mobile */}

                <div className="row g-2 mb-3">

                  <div className="col-6">

                    <label className="form-label small text-muted">
                      Email
                    </label>

                    <input
                      type="email"
                      className="form-control"
                      value={form.email}
                      onChange={(event) =>
                        setForm(
                          (current) => ({
                            ...current,
                            email:
                              event.target
                                .value,
                          })
                        )
                      }
                    />

                  </div>


                  <div className="col-6">

                    <label className="form-label small text-muted">
                      Mobile
                    </label>

                    <input
                      className="form-control"
                      value={form.mobile}
                      onChange={(event) =>
                        setForm(
                          (current) => ({
                            ...current,
                            mobile:
                              event.target
                                .value,
                          })
                        )
                      }
                    />

                  </div>

                </div>


                {/* Courses */}

                <div className="mb-3">

                  <label className="form-label small text-muted d-block">
                    Courses
                  </label>

                  {courseNames.map(
                    (courseName) => (

                      <div
                        className="form-check form-check-inline"
                        key={courseName}
                      >

                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={form.courses.includes(
                            courseName
                          )}
                          onChange={() =>
                            toggleCourse(
                              courseName
                            )
                          }
                          id={`add-course-${courseName}`}
                        />

                        <label
                          className="form-check-label small"
                          htmlFor={`add-course-${courseName}`}
                        >
                          {courseName}
                        </label>

                      </div>

                    )
                  )}

                </div>


                {/* Status */}

                <div className="mb-2">

                  <label className="form-label small text-muted">
                    Status
                  </label>

                  <select
                    className="form-select"
                    value={form.status}
                    onChange={(event) =>
                      setForm(
                        (current) => ({
                          ...current,
                          status:
                            event.target
                              .value,
                        })
                      )
                    }
                  >

                    <option value="Active">
                      Active
                    </option>

                    <option value="Inactive">
                      Inactive
                    </option>

                  </select>

                </div>


                {formError && (
                  <div className="text-danger small mt-2">
                    {formError}
                  </div>
                )}

              </div>


              <div className="modal-footer">

                <button
                  className="btn btn-outline-secondary"
                  onClick={() =>
                    setShowAdd(false)
                  }
                >
                  Cancel
                </button>

                <button
                  className="btn btn-primary"
                  onClick={
                    saveNewStudent
                  }
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : "Add Student"}
                </button>

              </div>

            </div>

          </div>

        </div>

      )}


      {/* ======================================================
          SEND MESSAGE MODAL
      ====================================================== */}

      {showMessage && (

        <div
          className="send-msg-backdrop"
          onClick={closeMessageModal}
        >

          <div
            className="send-msg-modal"
            onClick={(event) =>
              event.stopPropagation()
            }
          >

            {/* Header */}

            <div className="send-msg-header">

              <div className="send-msg-icon">
                <i className="bi bi-envelope-fill" />
              </div>

              <div className="send-msg-titles">

                <h5 className="send-msg-title">
                  Send Message
                </h5>

                <p className="send-msg-sub">
                  Send an email to a selected student.
                </p>

              </div>

              <button
                className="send-msg-close"
                onClick={
                  closeMessageModal
                }
                aria-label="Close"
              >
                <i className="bi bi-x-lg" />
              </button>

            </div>


            {/* Body */}

            <div className="send-msg-body">

              {/* Success */}

              {messageSent && (

                <div className="admin-login-success mb-3">
                  Message sent successfully.
                </div>

              )}


              {/* ==================================================
                  STUDENT
              ================================================== */}

              <div className="send-msg-group">

                <label className="send-msg-label">
                  Student
                </label>

                <select
                  className="form-select"
                  value={
                    messageForm.studentId
                  }
                  onChange={
                    handleMessageStudentChange
                  }
                  disabled={
                    sendingMessage
                  }
                >

                  <option value="">
                    Select a student
                  </option>

                  {students.map(
                    (student) => (

                      <option
                        key={student.id}
                        value={student.id}
                      >
                        {student.name ||
                          student.email ||
                          `Student ${student.id}`}
                        {" — "}
                        {student.email}
                      </option>

                    )
                  )}

                </select>

              </div>


              {/* ==================================================
                  EMAIL
              ================================================== */}

              <div className="send-msg-group">

                <label className="send-msg-label">
                  Email
                </label>

                <input
                  type="email"
                  className="form-control"
                  value={
                    messageForm.email
                  }
                  readOnly
                  placeholder="Student email"
                />

              </div>


              {/* ==================================================
                  SUBJECT
              ================================================== */}

              <div className="send-msg-group">

                <label className="send-msg-label">
                  Subject
                </label>

                <input
                  className="form-control"
                  placeholder="Exam Instructions"
                  value={
                    messageForm.subject
                  }
                  onChange={(event) =>
                    updateMessageField(
                      "subject",
                      event.target.value
                    )
                  }
                  disabled={
                    sendingMessage
                  }
                />

              </div>


              {/* ==================================================
                  MESSAGE
              ================================================== */}

              <div className="send-msg-group">

                <label className="send-msg-label">
                  Message
                </label>

                <textarea
                  className="form-control send-msg-textarea"
                  rows={6}
                  maxLength={
                    MESSAGE_MAX_LEN
                  }
                  placeholder={
                    "Hello,\n\nYour exam is scheduled for..."
                  }
                  value={
                    messageForm.body
                  }
                  onChange={(event) =>
                    updateMessageField(
                      "body",
                      event.target.value
                    )
                  }
                  disabled={
                    sendingMessage
                  }
                />

                <div className="send-msg-meta">

                  <span className="send-msg-counter">
                    {
                      messageForm.body
                        .length
                    }
                    /
                    {MESSAGE_MAX_LEN}
                  </span>

                </div>

              </div>


              {/* ==================================================
                  EXAM PDF
              ================================================== */}

              <div className="send-msg-group">

                <div className="form-check">

                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="include-exam-pdf"
                    checked={
                      messageForm.includeExamPdf
                    }
                    disabled={
                      sendingMessage ||
                      !messageForm.studentId
                    }
                    onChange={(event) =>
                      setMessageForm(
                        (current) => ({
                          ...current,
                          includeExamPdf:
                            event.target
                              .checked,
                          attemptId:
                            event.target
                              .checked
                              ? current.attemptId
                              : "",
                        })
                      )
                    }
                  />

                  <label
                    className="form-check-label"
                    htmlFor="include-exam-pdf"
                  >
                    Attach Exam PDF
                  </label>

                </div>

              </div>


              {/* ==================================================
                  EXAM ATTEMPT
              ================================================== */}

              {messageForm.includeExamPdf && (

                <div className="send-msg-group">

                  <label className="send-msg-label">
                    Exam Attempt
                  </label>


                  {loadingAttempts ? (

                    <div className="text-muted small">
                      Loading exam attempts...
                    </div>

                  ) : examAttempts.length ===
                    0 ? (

                    <div className="alert alert-warning py-2 small mb-0">
                      No exam attempts were found for this student.
                    </div>

                  ) : (

                    <select
                      className="form-select"
                      value={
                        messageForm.attemptId
                      }
                      onChange={(event) =>
                        updateMessageField(
                          "attemptId",
                          event.target
                            .value
                        )
                      }
                      disabled={
                        sendingMessage
                      }
                    >

                      <option value="">
                        Select exam attempt
                      </option>

                      {examAttempts.map(
                        (attempt) => (

                          <option
                            key={
                              attempt.id
                            }
                            value={
                              attempt.id
                            }
                          >
                            {attempt.exam_name ||
                              `Exam #${attempt.exam_id}`}
                            {" — "}
                            {attempt.status}
                            {" — "}
                            {formatAttemptDate(
                              attempt.started_at
                            )}
                          </option>

                        )
                      )}

                    </select>

                  )}

                </div>

              )}


              {/* ==================================================
                  ERROR
              ================================================== */}

              {messageError && (

                <div className="send-msg-error">
                  {messageError}
                </div>

              )}

            </div>


            {/* Footer */}

            <div className="send-msg-footer">

              <button
                className="btn btn-outline-secondary"
                onClick={
                  closeMessageModal
                }
                disabled={
                  sendingMessage
                }
              >
                Cancel
              </button>


              <button
                className="btn btn-primary send-msg-send-btn"
                onClick={
                  handleSendMessage
                }
                disabled={
                  sendingMessage ||
                  messageSent
                }
              >

                <i className="bi bi-send" />

                {sendingMessage
                  ? "Sending..."
                  : "Send"}

              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}